import React, { useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      const next = params.get("next");
      const redirect =
        next ||
        location.state?.from ||
        (u.role === "admin" ? "/admin" : "/dashboard");
      navigate(redirect, { replace: true });
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12 fade-in" data-testid="login-page">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[color:var(--line)] p-8 shadow-sm">
        <h1 className="font-serif text-3xl mb-2 text-[color:var(--text-primary)]">Welcome back</h1>
        <p className="text-sm text-[color:var(--text-muted)] mb-6">
          Sign in to continue your conflict resolution journey.
        </p>

        <button
          type="button"
          onClick={handleGoogle}
          data-testid="google-signin-btn"
          className="w-full mb-4 flex items-center justify-center gap-3 px-4 py-3 rounded-full border border-[color:var(--line)] bg-white hover:bg-[color:var(--surface-sage)] text-[color:var(--text-primary)] font-medium transition"
        >
          <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden>
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 3l5.7-5.7C34 6.1 29.2 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.7 1.1 7.8 3l5.7-5.7C34 6.1 29.2 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.2 2.4-5.2 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.7 16.2 44 24 44z"/>
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.5l6.2 5.2C40.9 35.6 44 30.2 44 24c0-1.2-.1-2.3-.4-3.5z"/>
          </svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-3 my-4 text-xs text-[color:var(--text-muted)]">
          <span className="h-px flex-1 bg-[color:var(--line)]" />
          or sign in with email
          <span className="h-px flex-1 bg-[color:var(--line)]" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-[color:var(--text-muted)] mb-1 block">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-[color:var(--line)] bg-white outline-none focus:ring-2 focus:ring-[color:var(--participant-a)]/30"
              data-testid="login-email"
            />
          </div>
          <div>
            <label className="text-sm text-[color:var(--text-muted)] mb-1 block">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-[color:var(--line)] bg-white outline-none focus:ring-2 focus:ring-[color:var(--participant-a)]/30"
              data-testid="login-password"
            />
          </div>
          {error && (
            <div className="text-sm text-[color:var(--counter-error)]" data-testid="login-error">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full px-4 py-3 rounded-full bg-[color:var(--participant-a)] text-white font-semibold hover:opacity-90 transition disabled:opacity-50"
            data-testid="login-submit"
          >
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>

        <p className="text-sm text-[color:var(--text-muted)] mt-6 text-center">
          New to FairResolver?{" "}
          <Link to="/register" className="font-medium text-[color:var(--participant-a)] hover:underline" data-testid="login-register-link">
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
